import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import TransformSelector from './TransformSelector';
import * as api from './api';
import { TIERS, TIER_BY_INDEX, tierCostLabel, tierCostStacked, abilityCostLabel } from './transformConstants';
import { jsonToPayload, transformToJson } from './transformJson';
import './TransformEditor.css';

// Monotonic client-side id so React keys stay stable for abilities that don't yet
// have a skillId (newly added) or that share a skillId is irrelevant here.
let uidCounter = 1;
const nextUid = () => uidCounter++;

// Map an API transform into the editor's working shape.
function toWorkingAbilities(abilities) {
    return (abilities || []).map((a) => ({
        uid: nextUid(),
        skillId: a.skillId ?? null,
        name: a.name || '',
        notes: a.notes || '',
        tier: a.tier ?? 0,
        isPassive: !!a.isPassive,
        isDefaultUnlocked: !!a.isDefaultUnlocked,
        sharedCount: a.sharedCount ?? 1,
    }));
}

export default function TransformEditor({ token }) {
    const [transforms, setTransforms] = useState([]);
    const [current, setCurrent] = useState(null); // { transformId, name, description, costume, abilities[] }
    const [originalById, setOriginalById] = useState({}); // skillId -> { name, notes } at load/save time
    const [saving, setSaving] = useState(false);

    // Modals
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState('');
    const [showImport, setShowImport] = useState(false);
    const [importText, setImportText] = useState('');
    const [exportText, setExportText] = useState(null);
    const [attachTier, setAttachTier] = useState(null); // tier index the picker will attach into
    const [abilityPool, setAbilityPool] = useState([]);
    const [poolQuery, setPoolQuery] = useState('');
    const [pendingShares, setPendingShares] = useState(null); // [{ uid, name, sharedCount, choice }]

    const createInputRef = useRef(null);

    api.setToken(token);

    const refreshList = async () => {
        const list = await api.fetchTransforms();
        setTransforms(Array.isArray(list) ? list : []);
        return list;
    };

    useEffect(() => {
        (async () => {
            try {
                await refreshList();
            } catch {
                toast.error('Failed to load transforms.');
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    useEffect(() => {
        if (showCreate) setTimeout(() => createInputRef.current?.focus(), 0);
    }, [showCreate]);

    // Load a saved/returned transform into editable state and snapshot its
    // abilities' name/notes so we can later detect shared-ability edits.
    const loadIntoState = (t) => {
        const abilities = toWorkingAbilities(t.abilities);
        setCurrent({
            transformId: t.transformId,
            name: t.name || '',
            description: t.description || '',
            costume: t.costume || '',
            abilities,
        });
        const orig = {};
        abilities.forEach((a) => { if (a.skillId != null) orig[a.skillId] = { name: a.name, notes: a.notes }; });
        setOriginalById(orig);
    };

    const chooseTransform = async (item) => {
        try {
            const full = await api.fetchTransform(item.transformId);
            loadIntoState(full);
        } catch {
            toast.error('Failed to load transform.');
        }
    };

    // ===== Transform-level field edits =====
    const setField = (field, value) => setCurrent((c) => ({ ...c, [field]: value }));

    // ===== Ability edits =====
    const patchAbility = (uid, patch) =>
        setCurrent((c) => ({
            ...c,
            abilities: c.abilities.map((a) => (a.uid === uid ? { ...a, ...patch } : a)),
        }));

    const removeAbility = (uid) =>
        setCurrent((c) => ({ ...c, abilities: c.abilities.filter((a) => a.uid !== uid) }));

    const addBlankAbility = (tierIndex) =>
        setCurrent((c) => ({
            ...c,
            abilities: [
                ...c.abilities,
                { uid: nextUid(), skillId: null, name: '', notes: '', tier: tierIndex, isPassive: false, isDefaultUnlocked: false, sharedCount: 1 },
            ],
        }));

    // ===== Create new transform =====
    const openCreate = () => { setNewName(''); setShowCreate(true); };
    const confirmCreate = async () => {
        const name = newName.trim();
        if (!name) { toast.warn('Please enter a name.'); return; }
        try {
            setSaving(true);
            const created = await api.createTransform({ name, description: '', costume: '', abilities: [] });
            loadIntoState(created);
            await refreshList();
            setShowCreate(false);
            toast.success(`Created "${name}"`);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to create transform.');
        } finally {
            setSaving(false);
        }
    };

    // ===== Delete =====
    const handleDelete = async () => {
        if (!current?.transformId) return;
        try {
            await api.deleteTransform(current.transformId);
            setCurrent(null);
            setOriginalById({});
            await refreshList();
            toast.success('Transform deleted.');
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to delete transform.');
        }
    };

    // ===== Attach existing ability =====
    const openAttach = async (tierIndex) => {
        setAttachTier(tierIndex);
        setPoolQuery('');
        try {
            const pool = await api.fetchAbilities();
            setAbilityPool(Array.isArray(pool) ? pool : []);
        } catch {
            toast.error('Failed to load the ability pool.');
            setAbilityPool([]);
        }
    };
    const attachAbility = (poolItem) => {
        // Treat an attached existing ability as shared, so a later name/notes edit
        // prompts the share-vs-fork choice. The real count is refreshed on save.
        setCurrent((c) => ({
            ...c,
            abilities: [
                ...c.abilities,
                {
                    uid: nextUid(),
                    skillId: poolItem.skillId,
                    name: poolItem.name || '',
                    notes: poolItem.notes || '',
                    tier: attachTier,
                    isPassive: false,
                    isDefaultUnlocked: false,
                    sharedCount: 2,
                },
            ],
        }));
        setOriginalById((m) => ({ ...m, [poolItem.skillId]: { name: poolItem.name || '', notes: poolItem.notes || '' } }));
        setAttachTier(null);
    };

    // ===== Import / Export JSON =====
    const importTransform = async (replaceCurrent) => {
        let payload;
        try {
            payload = jsonToPayload(JSON.parse(importText));
        } catch (e) {
            toast.error(`Invalid JSON: ${e.message}`);
            return;
        }
        try {
            setSaving(true);
            const saved = replaceCurrent
                ? await api.updateTransform(current.transformId, payload)
                : await api.createTransform(payload);
            loadIntoState(saved);
            await refreshList();
            setShowImport(false);
            setImportText('');
            toast.success(replaceCurrent ? 'Transform replaced from JSON.' : `Imported "${saved.name}".`);
        } catch (e) {
            toast.error(e?.response?.data?.error || 'Failed to import transform.');
        } finally {
            setSaving(false);
        }
    };

    const openExport = () => setExportText(JSON.stringify(transformToJson(current), null, 4));
    const copyExport = async () => {
        try { await navigator.clipboard.writeText(exportText); toast.success('Copied to clipboard.'); }
        catch { toast.warn('Could not copy — select and copy manually.'); }
    };
    const downloadExport = () => {
        const blob = new Blob([exportText], { type: 'application/json' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${(current.name || 'transform').replace(/[^a-z0-9-_]+/gi, '-').toLowerCase()}.json`;
        a.click();
        URL.revokeObjectURL(a.href);
    };

    // ===== Save (with share-vs-fork resolution) =====
    const abilityChanged = (a) => {
        if (a.skillId == null) return false;
        const orig = originalById[a.skillId];
        return orig && (orig.name !== a.name || orig.notes !== a.notes);
    };

    const handleSave = () => {
        if (!current) return;
        if (!current.name.trim()) { toast.warn('Name is required.'); return; }
        if (current.abilities.some((a) => !a.name.trim())) { toast.warn('Every ability needs a name.'); return; }

        // Edits to a shared ability's name/notes need an explicit share-or-fork choice.
        const prompts = current.abilities.filter((a) => abilityChanged(a) && a.sharedCount > 1);
        if (prompts.length) {
            setPendingShares(prompts.map((a) => ({ uid: a.uid, name: a.name, sharedCount: a.sharedCount, choice: 'share' })));
            return;
        }
        performSave({});
    };

    const performSave = async (decisions) => {
        setSaving(true);
        setPendingShares(null);
        try {
            // Work on a copy so fork-induced skillId swaps are reflected in the payload.
            const abilities = current.abilities.map((a) => ({ ...a }));
            for (const a of abilities) {
                if (!abilityChanged(a)) continue;
                const choice = decisions[a.uid] || 'share'; // non-shared edits just update in place
                if (choice === 'fork') {
                    const created = await api.createAbility({ name: a.name.trim(), notes: a.notes || '' });
                    a.skillId = created.skillId;
                    a.sharedCount = 1;
                } else {
                    await api.updateAbility(a.skillId, { name: a.name.trim(), notes: a.notes || '' });
                }
            }

            const payload = {
                name: current.name.trim(),
                description: current.description || '',
                costume: current.costume || '',
                abilities: abilities.map((a) => ({
                    skillId: a.skillId ?? undefined,
                    name: a.name.trim(),
                    notes: a.notes || '',
                    tier: a.tier,
                    isPassive: !!a.isPassive,
                    isDefaultUnlocked: !!a.isDefaultUnlocked,
                })),
            };

            const saved = current.transformId
                ? await api.updateTransform(current.transformId, payload)
                : await api.createTransform(payload);
            loadIntoState(saved);
            await refreshList();
            toast.success('Transform saved.');
        } catch (e) {
            console.error('Failed to save transform', e);
            toast.error(e?.response?.data?.error || 'Failed to save transform.');
        } finally {
            setSaving(false);
        }
    };

    // Names of the *other* transforms that share a given ability's skill, derived
    // from the already-loaded transform list (excludes the one being edited).
    const sharedTransformNames = (skillId) => {
        if (skillId == null) return [];
        return transforms
            .filter((t) => t.transformId !== current?.transformId && (t.abilities || []).some((a) => a.skillId === skillId))
            .map((t) => t.name);
    };

    const filteredPool = abilityPool.filter((p) => {
        const q = poolQuery.trim().toLowerCase();
        if (!q) return true;
        return (p.name || '').toLowerCase().includes(q);
    });
    const attachedSkillIds = new Set((current?.abilities || []).map((a) => a.skillId).filter((id) => id != null));

    return (
        <div className="transform-editor character-select-container">
            <h1 className="landing-title">Transform Editor</h1>

            <div className="transform-toolbar">
                <TransformSelector
                    transforms={transforms}
                    currentName={current?.name || ''}
                    onChoose={chooseTransform}
                    onCreateNew={openCreate}
                    disabled={saving}
                />
                <button className="tbtn" onClick={() => { setImportText(''); setShowImport(true); }}>
                    ⇪ Import JSON
                </button>
            </div>

            {current && (
                <>
                    <div className="transform-actions">
                        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save Changes'}
                        </button>
                        <button className="btn btn-secondary" onClick={openExport} disabled={saving}>Export JSON</button>
                        {current.transformId && (
                            <ConfirmButton
                                className="btn btn-danger transform-delete"
                                confirmClassName="is-confirming"
                                idleContent="Delete"
                                confirmContent="Delete?"
                                title="Delete this transform"
                                onConfirm={handleDelete}
                                disabled={saving}
                            />
                        )}
                    </div>

                    <div className="transform-fields">
                        <label className="tfield">
                            <span className="tfield-label">Name</span>
                            <input className="tinput tinput-name" value={current.name} onChange={(e) => setField('name', e.target.value)} />
                        </label>
                        <label className="tfield">
                            <span className="tfield-label">Description</span>
                            <AutoGrowTextarea className="tinput" rows={2} value={current.description} onChange={(e) => setField('description', e.target.value)} />
                        </label>
                        <label className="tfield">
                            <span className="tfield-label">Costuming</span>
                            <AutoGrowTextarea className="tinput" rows={2} value={current.costume} onChange={(e) => setField('costume', e.target.value)} />
                        </label>
                    </div>

                    <div className="tier-list">
                        {TIERS.map((tier) => {
                            const abilities = current.abilities.filter((a) => a.tier === tier.index);
                            const cost = tierCostLabel(tier.key);
                            return (
                                <section key={tier.key} className={`tier-group tier-${tier.key}`}>
                                    <div className="tier-vlabel" title={cost}>
                                        <div className="tier-vlabel-rot">
                                            <span className="tier-vlabel-name">{tier.label}</span>
                                            <span className="tier-vlabel-cost">{tierCostStacked(tier.key)}</span>
                                        </div>
                                        <TierAddMenu
                                            onAddNew={() => addBlankAbility(tier.index)}
                                            onAttach={() => openAttach(tier.index)}
                                        />
                                    </div>
                                    <div className="tier-body">
                                        {abilities.length === 0 && (
                                            <p className="tier-empty">No abilities in this tier.</p>
                                        )}

                                        {abilities.map((a) => (
                                            <AbilityCard
                                                key={a.uid}
                                                ability={a}
                                                tierKey={tier.key}
                                                sharedWith={sharedTransformNames(a.skillId)}
                                                onPatch={patchAbility}
                                                onRemove={removeAbility}
                                            />
                                        ))}
                                    </div>
                                </section>
                            );
                        })}
                    </div>
                </>
            )}

            {/* ===== Create modal ===== */}
            {showCreate && (
                <ModalOverlay onClose={() => setShowCreate(false)}>
                    <h3 className="modal-title">Create New Transform</h3>
                    <label className="modal-label" htmlFor="newTransformName">Name</label>
                    <input
                        id="newTransformName"
                        ref={createInputRef}
                        className="modal-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') confirmCreate(); }}
                        placeholder="e.g. Gargoyle, Greater"
                    />
                    <div className="modal-actions">
                        <button className="modal-btn modal-primary" onClick={confirmCreate} disabled={saving || !newName.trim()}>
                            {saving ? 'Creating…' : 'Create'}
                        </button>
                        <button className="modal-btn modal-secondary" onClick={() => setShowCreate(false)} disabled={saving}>Cancel</button>
                    </div>
                </ModalOverlay>
            )}

            {/* ===== Import modal ===== */}
            {showImport && (
                <ModalOverlay onClose={() => setShowImport(false)} wide>
                    <h3 className="modal-title">Import Transform JSON</h3>
                    <textarea
                        className="modal-input modal-textarea"
                        rows={14}
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        placeholder='{ "name": "…", "description": "…", "costume": "…", "abilities": { "static": [ … ] } }'
                    />
                    <div className="modal-actions">
                        <button className="modal-btn modal-primary" onClick={() => importTransform(false)} disabled={saving || !importText.trim()}>
                            Import as New
                        </button>
                        {current?.transformId && (
                            <button className="modal-btn modal-secondary" onClick={() => importTransform(true)} disabled={saving || !importText.trim()}>
                                Replace “{current.name}”
                            </button>
                        )}
                        <button className="modal-btn modal-secondary" onClick={() => setShowImport(false)} disabled={saving}>Cancel</button>
                    </div>
                    {current?.transformId && (
                        <p className="modal-hint">“Replace” overwrites the currently selected transform's fields and abilities.</p>
                    )}
                </ModalOverlay>
            )}

            {/* ===== Export modal ===== */}
            {exportText != null && (
                <ModalOverlay onClose={() => setExportText(null)} wide>
                    <h3 className="modal-title">Export Transform JSON</h3>
                    <textarea className="modal-input modal-textarea" rows={16} value={exportText} readOnly />
                    <div className="modal-actions">
                        <button className="modal-btn modal-primary" onClick={copyExport}>Copy</button>
                        <button className="modal-btn modal-secondary" onClick={downloadExport}>Download</button>
                        <button className="modal-btn modal-secondary" onClick={() => setExportText(null)}>Close</button>
                    </div>
                </ModalOverlay>
            )}

            {/* ===== Attach existing ability modal ===== */}
            {attachTier != null && (
                <ModalOverlay onClose={() => setAttachTier(null)} wide>
                    <h3 className="modal-title">Attach Existing Ability — {TIER_BY_INDEX[attachTier]?.label}</h3>
                    <input
                        className="modal-input"
                        value={poolQuery}
                        onChange={(e) => setPoolQuery(e.target.value)}
                        placeholder="Search abilities…"
                        autoFocus
                    />
                    <div className="attach-list">
                        {filteredPool.length === 0 && <p className="modal-hint">No matching abilities.</p>}
                        {filteredPool.map((p) => {
                            const already = attachedSkillIds.has(p.skillId);
                            return (
                                <button
                                    key={p.skillId}
                                    className="attach-item"
                                    onClick={() => attachAbility(p)}
                                    disabled={already}
                                    title={already ? 'Already attached to this transform' : p.notes || ''}
                                >
                                    <span className="attach-name">{p.name}</span>
                                    {already && <span className="attach-tag">attached</span>}
                                </button>
                            );
                        })}
                    </div>
                    <div className="modal-actions">
                        <button className="modal-btn modal-secondary" onClick={() => setAttachTier(null)}>Done</button>
                    </div>
                </ModalOverlay>
            )}

            {/* ===== Share-vs-fork modal ===== */}
            {pendingShares && (
                <ModalOverlay onClose={() => setPendingShares(null)} wide>
                    <h3 className="modal-title">Shared Abilities Edited</h3>
                    <p className="modal-hint">
                        These abilities are used by more than one transform. Choose whether each edit should
                        apply everywhere, or become a copy unique to this transform.
                    </p>
                    <div className="share-list">
                        {pendingShares.map((p, i) => (
                            <div key={p.uid} className="share-row">
                                <div className="share-name">{p.name} <span className="share-count">used by {p.sharedCount} transforms</span></div>
                                <div className="share-choices">
                                    <label>
                                        <input
                                            type="radio"
                                            name={`share-${p.uid}`}
                                            checked={p.choice === 'share'}
                                            onChange={() => setPendingShares((list) => list.map((x, j) => (j === i ? { ...x, choice: 'share' } : x)))}
                                        />
                                        Update everywhere
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name={`share-${p.uid}`}
                                            checked={p.choice === 'fork'}
                                            onChange={() => setPendingShares((list) => list.map((x, j) => (j === i ? { ...x, choice: 'fork' } : x)))}
                                        />
                                        Copy for this transform
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="modal-actions">
                        <button
                            className="modal-btn modal-primary"
                            onClick={() => {
                                const decisions = {};
                                pendingShares.forEach((p) => { decisions[p.uid] = p.choice; });
                                performSave(decisions);
                            }}
                            disabled={saving}
                        >
                            {saving ? 'Saving…' : 'Confirm & Save'}
                        </button>
                        <button className="modal-btn modal-secondary" onClick={() => setPendingShares(null)} disabled={saving}>Cancel</button>
                    </div>
                </ModalOverlay>
            )}

            <ToastContainer position="bottom-right" autoClose={3000} />
        </div>
    );
}

// Textarea that grows to fit its content instead of scrolling.
function AutoGrowTextarea({ value, className = '', ...props }) {
    const ref = useRef(null);
    const fit = () => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    };
    useLayoutEffect(fit, [value]);
    useEffect(() => {
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
    }, []);
    return <textarea ref={ref} className={`${className} autogrow`.trim()} value={value} {...props} />;
}

// Two-stage confirm button: first click arms it (showing confirmContent); a
// second click — only after a short arming delay, so a rapid double-click can't
// fire it — runs onConfirm. Any interaction elsewhere (click/scroll/key) or a
// blur collapses it back to the idle state.
function ConfirmButton({ className = '', confirmClassName = '', idleContent, confirmContent, onConfirm, title, ariaLabel, disabled, armMs = 220 }) {
    const [confirming, setConfirming] = useState(false);
    const armedRef = useRef(false);
    const btnRef = useRef(null);

    useEffect(() => {
        if (!confirming) return;
        armedRef.current = false;
        const armTimer = setTimeout(() => { armedRef.current = true; }, armMs);
        const collapse = (e) => {
            if (e && btnRef.current && btnRef.current.contains(e.target)) return; // ignore our own button
            setConfirming(false);
        };
        const collapseAlways = () => setConfirming(false);
        document.addEventListener('mousedown', collapse, true);
        document.addEventListener('keydown', collapseAlways, true);
        window.addEventListener('scroll', collapseAlways, true);
        return () => {
            clearTimeout(armTimer);
            document.removeEventListener('mousedown', collapse, true);
            document.removeEventListener('keydown', collapseAlways, true);
            window.removeEventListener('scroll', collapseAlways, true);
        };
    }, [confirming, armMs]);

    const handleClick = (e) => {
        e.stopPropagation();
        if (!confirming) { setConfirming(true); return; }
        if (!armedRef.current) return; // too fast — wait for the arm delay
        setConfirming(false);
        onConfirm();
    };

    return (
        <button
            ref={btnRef}
            type="button"
            className={`${className} ${confirming ? confirmClassName : ''}`.trim()}
            title={title}
            aria-label={ariaLabel}
            disabled={disabled}
            onClick={handleClick}
            onBlur={() => setConfirming(false)}
        >
            {confirming ? confirmContent : idleContent}
        </button>
    );
}

// The "+" control at the bottom of a tier label. Clicking it unrolls a small menu
// to add a brand-new ability or attach an existing one to this tier.
function TierAddMenu({ onAddNew, onAttach }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    return (
        <div className="tier-add" ref={ref}>
            <button
                type="button"
                className={`tier-add-btn ${open ? 'is-open' : ''}`}
                title="Add an ability to this tier"
                aria-label="Add an ability to this tier"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                +
            </button>
            <div className={`tier-add-menu ${open ? 'open' : ''}`}>
                <button type="button" onClick={() => { setOpen(false); onAddNew(); }}>Add New Ability</button>
                <button type="button" onClick={() => { setOpen(false); onAttach(); }}>Attach Existing Ability</button>
            </div>
        </div>
    );
}

// Popover tier picker, styled like the "+" add menu; the trigger keeps the gold
// square look of the add button.
function TierChangeMenu({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    return (
        <div className="tier-change" ref={ref}>
            <button
                type="button"
                className={`tier-change-btn ${open ? 'is-open' : ''}`}
                title="Change tier"
                aria-label="Change tier"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
            >
                ↕
            </button>
            <div className={`tier-change-menu ${open ? 'open' : ''}`}>
                {TIERS.map((t) => (
                    <button
                        key={t.key}
                        type="button"
                        className={t.index === value ? 'is-current' : ''}
                        onClick={() => { setOpen(false); onChange(t.index); }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

// "shared ×N" badge whose tooltip opens downward, or upward when there isn't
// enough room below it in the viewport.
function SharedBadge({ count, names }) {
    const [up, setUp] = useState(false);
    const ref = useRef(null);
    const decide = () => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const estimate = 44 + Math.max(names.length, 1) * 22; // title + rows
        setUp(window.innerHeight - rect.bottom < estimate);
    };
    return (
        <span
            ref={ref}
            className={`ability-shared ${up ? 'popover-up' : ''}`}
            tabIndex={0}
            onMouseEnter={decide}
            onFocus={decide}
        >
            shared ×{count}
            <span className="shared-popover" role="tooltip">
                <span className="shared-popover-title">Also used by</span>
                {names.length > 0 ? (
                    names.map((n, i) => <span key={i} className="shared-popover-item">{n}</span>)
                ) : (
                    <span className="shared-popover-item shared-popover-empty">another transform</span>
                )}
            </span>
        </span>
    );
}

// ===== Ability card =====
function AbilityCard({ ability, tierKey, sharedWith = [], onPatch, onRemove }) {
    const isStatic = tierKey === 'static';
    return (
        <div className={`ability-card ${isStatic ? 'is-static' : ''} ${ability.isPassive && !isStatic ? 'is-passive' : ''}`}>
            <div className="ability-leftcol">
                <span className="confirm-slot">
                    <ConfirmButton
                        className="ability-remove confirm-btn"
                        confirmClassName="is-confirming"
                        idleContent="✕"
                        confirmContent="Delete?"
                        title="Remove from this transform"
                        ariaLabel="Remove ability"
                        onConfirm={() => onRemove(ability.uid)}
                    />
                </span>
                <TierChangeMenu value={ability.tier} onChange={(idx) => onPatch(ability.uid, { tier: idx })} />
            </div>

            <div className="ability-main">
                <input
                    className="ability-name"
                    value={ability.name}
                    onChange={(e) => onPatch(ability.uid, { name: e.target.value })}
                    placeholder="Ability name"
                />
                <AutoGrowTextarea
                    className="ability-notes"
                    rows={2}
                    value={ability.notes}
                    onChange={(e) => onPatch(ability.uid, { notes: e.target.value })}
                    placeholder="Notes (one rule per line)"
                />
            </div>

            <div className="ability-side">
                <div className="ability-side-top">
                    {ability.sharedCount > 1 && (
                        <SharedBadge count={ability.sharedCount} names={sharedWith} />
                    )}
                    <span className="ability-cost">{abilityCostLabel(tierKey, ability.isPassive)}</span>
                </div>

                {!isStatic && (
                    <div className="ability-toggles">
                        <button
                            type="button"
                            className={`ability-toggle ${ability.isPassive ? 'on' : 'off'}`}
                            aria-pressed={ability.isPassive}
                            onClick={() => onPatch(ability.uid, { isPassive: !ability.isPassive })}
                        >
                            Passive
                        </button>
                        <button
                            type="button"
                            className={`ability-toggle ${ability.isDefaultUnlocked ? 'on' : 'off'}`}
                            aria-pressed={ability.isDefaultUnlocked}
                            onClick={() => onPatch(ability.uid, { isDefaultUnlocked: !ability.isDefaultUnlocked })}
                        >
                            Unlocked
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

// ===== Modal shell (matches the create-modal styling used elsewhere) =====
function ModalOverlay({ children, onClose, wide = false }) {
    return (
        <div
            className="create-modal-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
            role="dialog"
            aria-modal="true"
        >
            <div className={`create-modal ${wide ? 'modal-wide' : ''}`} onMouseDown={(e) => e.stopPropagation()}>
                {children}
            </div>
        </div>
    );
}
